import aiNetworkMaintenances from 'dashboard/api/aiNetworkMaintenances';
import { types as massivaTypes } from './massiva';

// A árvore da página de Comunicados cacheia o público congelado por id
// (studioMassiva.audienceByMaintenance). Escrita que RECONGELA o público
// (update/reschedule) — ou que tira a manutenção de cena (finish/destroy) —
// tem que derrubar aquela entrada, senão a página que audita o que a IA fala
// serve caixas/clientes de antes da edição pelo resto da sessão.
const invalidateNoticeAudience = (commit, id) =>
  commit(
    `studioMassiva/${massivaTypes.SET_MAINTENANCE_AUDIENCE}`,
    { id, entry: null },
    { root: true }
  );

// Manutenção programada (massiva PREVISTA). Mesmo padrão do `studioMassiva`:
// o estado mora aqui, os componentes são burros.
//
// O ESCOPO é o coração: o operador busca um circuito, vê as CTOs, e escolhe
// quais vão ser mexidas. Só no `create` isso vira registro — e o público é
// congelado nesse instante (o backend lê o ERP).
export const types = {
  SET_UI_FLAG: 'SET_MAINTENANCE_UI_FLAG',
  SET_LIST: 'SET_MAINTENANCE_LIST',
  SET_URA_SYNC_ENABLED: 'SET_MAINTENANCE_URA_SYNC_ENABLED',
  SET_INSTANCE: 'SET_MAINTENANCE_INSTANCE',
  SET_CIRCUIT: 'SET_MAINTENANCE_CIRCUIT',
  SET_CATALOG: 'SET_MAINTENANCE_CATALOG',
  SET_OPCOES: 'SET_MAINTENANCE_OPCOES',
  SET_CLIENTES: 'SET_MAINTENANCE_CLIENTES',
  RESET_TIPOS_CACHE: 'RESET_MAINTENANCE_TIPOS_CACHE',
  SET_SELECTION: 'SET_MAINTENANCE_SELECTION',
  TOGGLE_SELECTION: 'TOGGLE_MAINTENANCE_SELECTION',
  SET_AUDIENCE: 'SET_MAINTENANCE_AUDIENCE',
  SET_AUDIENCE_ERROR: 'SET_MAINTENANCE_AUDIENCE_ERROR',
  SET_ERROR: 'SET_MAINTENANCE_ERROR',
  SET_CURRENT: 'SET_MAINTENANCE_CURRENT',
  UPSERT_MAINTENANCE: 'UPSERT_MAINTENANCE',
  MERGE_AUDIENCE_STATE: 'MERGE_MAINTENANCE_AUDIENCE_STATE',
};

const state = {
  list: [],
  // A conta tem write-through de manutenção pra URA do ERP? É AUTOMÁTICO por
  // provedor suportado (hoje só SGP — o toggle opt-in foi removido em
  // 2026-07-16). Vem no `meta.ura_sync_enabled` da listagem. Sem ele a UI de
  // sync (badge/banner/resync) nem aparece — conta IXC fica idêntica ao
  // que já era.
  uraSyncEnabled: false,
  // Seletor "tipos" (SGP, fiel à tela nativa): opções de cada tipo de alvo
  // (POPs/NAS/OLTs/OLT PONs/CTO) cacheadas POR tipo. Cada payload é
  // { opcoes: [{ id, nome, contagem? }], colhido_em?, online_em?, parcial?,
  // warming?, capabilities }. É POR instância — zerado no setInstance/reset.
  //
  // `online_em` (aqui, em clientesPorAlvo, circuit e catalog.catalogo) = QUANDO
  // o "no ar" daquela resposta foi observado; `fora_do_ar_em` (mesmos objetos)
  // = idade do "fora do ar" não confirmado, quando a fonte mistura duas. A
  // tela mostra a idade ao lado do status (OnlineEmNote) — por isso os payloads são guardados INTEIROS: picar
  // só a lista perderia o carimbo e o status voltaria a ler como "agora".
  opcoesPorTipo: {},
  // Clientes de um alvo (chave tipada), pra hierarquia do Escopo (alvo →
  // clientes). Cacheado por chave: reexpandir o mesmo alvo não repete a ida à
  // rede. Também POR instância — zerado no setInstance/reset.
  clientesPorAlvo: {},
  // Instância do ERP escolhida no wizard (system_label). '' = deixa o backend
  // usar a primeira instância — retrocompat da conta 1-instância, onde o
  // seletor nem aparece. Conta multi-instância (SGP com N hosts) precisa da
  // escolha explícita: navegar "na primeira" em silêncio congelaria o público
  // do host errado.
  instance: '',
  // A manutenção aberta no detalhe. Depois de cada escrita a lista é relida e
  // esta referência acompanha (ver SET_LIST) — o drawer aberto não pode continuar
  // mostrando o estado anterior ao aviso que acabou de sair.
  current: null,
  // Resultado da busca por circuito:
  // { conexao, total, caixas: [...], online_em?, fora_do_ar_em? }
  circuit: null,
  // Catálogo de agrupadores (combobox): { agrupadores, colhido_em, warming,
  // capabilities, error? } — cacheado no Go; warming = colheita em curso.
  catalog: null,
  // O escopo: CTOs escolhidas (chave = caixa_ftth). MULTISELECT (Fase 3):
  // caixas de N circuitos convivem — cada uma carimba a própria conexao e o
  // system_label da instância que respondeu (é dela que o create deriva a
  // instância da manutenção).
  selection: {},
  // Prévia do público (quem não tem telefone, limite do canal).
  audience: null,
  // Separado do `error`: o erro do público é do DETALHE (banner no drawer), o
  // `error` é do escopo/escrita. Um sobrescrevendo o outro faria o drawer acusar
  // uma falha do ERP que era do cadastro anterior.
  audienceError: '',
  // Erro NUNCA vira lista vazia: "0 CTOs" seria lido como "circuito sem caixa"
  // e o operador cadastraria uma manutenção que não avisa ninguém.
  error: '',
  uiFlags: {
    fetchingList: false,
    fetchingCircuit: false,
    saving: false,
    fetchingAudience: false,
    notifying: false,
    resyncing: false,
    refreezing: false,
  },
};

const getters = {
  getList: $state => $state.list,
  getUraSyncEnabled: $state => $state.uraSyncEnabled,
  getInstance: $state => $state.instance,
  getCurrent: $state => $state.current,
  getCircuit: $state => $state.circuit,
  getCatalog: $state => $state.catalog,
  getOpcoesPorTipo: $state => $state.opcoesPorTipo,
  getClientesPorAlvo: $state => $state.clientesPorAlvo,
  getSelection: $state => $state.selection,
  getSelectionBoxes: $state => Object.values($state.selection),
  getAudience: $state => $state.audience,
  getAudienceError: $state => $state.audienceError,
  getError: $state => $state.error,
  getUIFlags: $state => $state.uiFlags,
  // Quantos clientes a manutenção vai DERRUBAR (os que estão no ar). Quem já
  // está caído não cai duas vezes — mas segue no público (a manutenção explica
  // por que ele continua sem internet).
  getSelectionImpact: $state =>
    Object.values($state.selection).reduce(
      (acc, box) => ({
        total: acc.total + (box.total || 0),
        online: acc.online + (box.online || 0),
      }),
      { total: 0, online: 0 }
    ),
};

// De QUANDO é o "no ar" da caixa: carimbado ao entrar na seleção, a partir da
// resposta que forneceu a contagem (o resumo "Escopo escolhido" mostra o mais
// antigo — ver onlineEm.js). Caixa que já chega carimbada (modo tipos carimba
// com o payload das opções) mantém o dela; senão vem do circuito (IXC).
// Não vai pro backend: o `create` monta as caixas campo a campo.
const stampStatusAge = (box, circuit) => ({
  online_em: 'online_em' in box ? box.online_em : circuit?.online_em || '',
  fora_do_ar_em:
    'fora_do_ar_em' in box ? box.fora_do_ar_em : circuit?.fora_do_ar_em || '',
});

const apiErrorMessage = error =>
  error.response?.data?.error || error.message || '';

// O que o evento do congelamento assíncrono pode mexer na linha. Allowlist de
// propósito: o payload do broadcast é um RESUMO (não o :show inteiro) e fundir
// tudo às cegas apagaria com `undefined` campos que o evento nem conhece.
const AUDIENCE_EVENT_FIELDS = [
  'audience_status',
  'audience_error',
  'snapshot_at',
  'clientes_total',
  'clientes_online',
  // Se o backend um dia carimbar as contagens no evento, elas entram e a
  // releitura da lista vira redundante (não errada).
  'audience_size',
  'unreachable_size',
];

const actions = {
  async fetchList({ commit, rootGetters }) {
    commit(types.SET_UI_FLAG, { fetchingList: true });
    try {
      const { data } = await aiNetworkMaintenances.list(
        rootGetters.getCurrentAccountId
      );
      commit(types.SET_LIST, data.payload || []);
      // O toggle da conta chega no `meta` do topo. Ausência (conta sem o
      // recurso) lê como desligado — a UI de sync some, sem estourar.
      commit(types.SET_URA_SYNC_ENABLED, Boolean(data.meta?.ura_sync_enabled));
    } finally {
      commit(types.SET_UI_FLAG, { fetchingList: false });
    }
  },

  // Opções de UM tipo de alvo (seletor "tipos", fiel à tela do SGP). Cacheadas
  // por tipo — a instância é a mesma (trocar de instância zera o cache inteiro).
  // Erro PROPAGA pro componente (que grita e oferece retry) e NUNCA vira lista
  // vazia comitada: sem try/catch, uma falha nunca chega ao commit — "0 opções"
  // leria como "provedor sem POP" e o operador cadastraria sem alvo.
  async fetchOpcoes({ commit, state: $state, rootGetters }, tipo) {
    const { data } = await aiNetworkMaintenances.opcoes(
      rootGetters.getCurrentAccountId,
      tipo,
      $state.instance
    );
    commit(types.SET_OPCOES, { tipo, data });
    return data;
  },

  // Clientes de um alvo (chave tipada) pra hierarquia do Escopo. Respondem do
  // censo LOCAL — rápido. Cacheado por chave: reexpandir o mesmo alvo não repete
  // a ida à rede. Erro PROPAGA (o componente mostra inline + retry).
  async fetchClientesDoAlvo({ commit, state: $state, rootGetters }, caixa) {
    if ($state.clientesPorAlvo[caixa]) return $state.clientesPorAlvo[caixa];

    const { data } = await aiNetworkMaintenances.clientes(
      rootGetters.getCurrentAccountId,
      caixa,
      $state.instance
    );
    commit(types.SET_CLIENTES, { caixa, data });
    return data;
  },

  // Escolher a instância do ERP (system_label) no wizard. A manutenção pertence
  // a UMA instância (D5): misturar caixas de instâncias diferentes congelaria o
  // público do ERP errado. Por isso trocar de instância ZERA catálogo, circuito
  // e escopo — recomeça a navegação naquele host. No-op quando o valor não muda
  // (não descarta o trabalho em curso à toa).
  setInstance({ commit, state: $state }, systemLabel) {
    const next = systemLabel || '';
    if (next === $state.instance) return;
    commit(types.SET_INSTANCE, next);
    commit(types.SET_CATALOG, null);
    commit(types.SET_CIRCUIT, null);
    commit(types.SET_SELECTION, {});
    // Opções e clientes do seletor "tipos" também são POR instância: trocar de
    // host tem que descartar o cache do anterior, senão o dropdown mostraria
    // POP/NAS/CTO de outra rede.
    commit(types.RESET_TIPOS_CACHE);
  },

  // Busca as CTOs de um circuito (ao vivo no ERP).
  // Catálogo pro combobox. Erro NÃO derruba a tela: vira `error` no catálogo
  // (o input de texto livre continua funcionando como fallback divulgado).
  // Leva a instância escolhida como dica: catálogo é POR instância.
  async fetchAgrupadores({ commit, state: $state, rootGetters }) {
    try {
      const { data } = await aiNetworkMaintenances.agrupadores(
        rootGetters.getCurrentAccountId,
        $state.instance
      );
      commit(types.SET_CATALOG, data);
      return data;
    } catch (error) {
      const message =
        error?.response?.data?.error || error?.message || 'erro desconhecido';
      commit(types.SET_CATALOG, { error: message });
      return null;
    }
  },

  async fetchCircuit({ commit, state: $state, rootGetters }, { conexao }) {
    commit(types.SET_UI_FLAG, { fetchingCircuit: true });
    commit(types.SET_ERROR, '');
    try {
      const { data } = await aiNetworkMaintenances.caixasDaConexao(
        rootGetters.getCurrentAccountId,
        conexao,
        $state.instance
      );

      commit(types.SET_CIRCUIT, data);
    } catch (error) {
      commit(types.SET_CIRCUIT, null);
      commit(types.SET_ERROR, apiErrorMessage(error));
      throw error;
    } finally {
      commit(types.SET_UI_FLAG, { fetchingCircuit: false });
    }
  },

  // Carimba a CONEXAO do circuito em foco na caixa: no multiselect cada
  // caixa do escopo sabe de qual circuito veio (vai pro banco e pro
  // merge field {{circuito}} por cliente).
  toggleBox({ commit, state: $state }, box) {
    commit(types.TOGGLE_SELECTION, {
      ...box,
      conexao: box.conexao || $state.circuit?.conexao,
      // A instância viaja com a caixa: o create não pode depender do circuit
      // transiente (uma busca FALHA depois da escolha o zera).
      system_label: box.system_label || $state.circuit?.system_label,
      ...stampStatusAge(box, $state.circuit),
    });
  },

  // Seleção em LOTE ("selecionar todas" as CTOs do circuito — pedido A4):
  // UM commit com o mapa novo. N dispatches de toggleBox seriam N cópias do
  // mapa (O(N²) em alocação) e N re-renders. Carimba conexao/system_label
  // igual ao toggleBox — o create não pode depender do circuit transiente.
  selectManyBoxes({ commit, state: $state }, boxes) {
    const next = { ...$state.selection };
    boxes.forEach(box => {
      next[box.caixa_ftth] = {
        ...box,
        conexao: box.conexao || $state.circuit?.conexao,
        system_label: box.system_label || $state.circuit?.system_label,
        ...stampStatusAge(box, $state.circuit),
      };
    });
    commit(types.SET_SELECTION, next);
  },

  deselectManyBoxes({ commit, state: $state }, boxes) {
    const next = { ...$state.selection };
    boxes.forEach(box => delete next[box.caixa_ftth]);
    commit(types.SET_SELECTION, next);
  },

  clearSelection({ commit }) {
    commit(types.SET_SELECTION, {});
    commit(types.SET_CIRCUIT, null);
  },

  // Ao (re)entrar na tela. O escopo é uma escolha de UMA sessão de trabalho:
  // sobreviver à navegação faria o operador reabrir a página dias depois com um
  // circuito e caixas que ele não lembra de ter escolhido — e cadastrar congela
  // esse público. A instância é parte do mesmo escopo transiente: zerar aqui
  // faz o wizard multi-instância pedir a escolha explícita a cada sessão (e
  // devolve o baseline '' pro resto da tela — retrocompat 1-instância).
  reset({ commit }) {
    commit(types.SET_SELECTION, {});
    commit(types.SET_CIRCUIT, null);
    commit(types.SET_INSTANCE, '');
    // Opções/clientes do seletor "tipos" são transientes por sessão/instância —
    // somem junto com o resto do escopo pra não vazar alvos de uma sessão
    // anterior.
    commit(types.RESET_TIPOS_CACHE);
    commit(types.SET_AUDIENCE, null);
    commit(types.SET_AUDIENCE_ERROR, '');
    commit(types.SET_CURRENT, null);
    commit(types.SET_ERROR, '');
  },

  // Abrir o detalhe de uma manutenção. O público é lido AGORA (a lista traz só os
  // totais): é ele que diz quantas pessoas o botão de enviar vai alcançar.
  async select({ commit, dispatch }, maintenance) {
    commit(types.SET_CURRENT, maintenance);
    commit(types.SET_AUDIENCE, null);
    commit(types.SET_AUDIENCE_ERROR, '');
    if (!maintenance) return;

    // A falha já vira banner no drawer (`audienceError`) — engolir aqui é só não
    // duplicar o grito, não escondê-lo.
    await dispatch('fetchAudience', maintenance.id).catch(() => {});
  },

  unselect({ commit }) {
    commit(types.SET_CURRENT, null);
    commit(types.SET_AUDIENCE, null);
    commit(types.SET_AUDIENCE_ERROR, '');
  },

  // Cadastrar CONGELA o público (o backend lê o ERP) e NÃO envia nada.
  //
  // Os alvos viajam nas próprias CAIXAS (chave tipada em `caixa_ftth` + nome
  // amigável em `conexao`): o seletor "tipos" carimba POP/NAS/OLT/PON/CTO como
  // caixa. NÃO se manda `erp_targets` — o write-through da URA resolve os alvos
  // a partir das caixas no backend (o request não adultera a base do diff).
  async create({ commit, dispatch, state: $state, rootGetters }, payload) {
    commit(types.SET_UI_FLAG, { saving: true });
    commit(types.SET_ERROR, '');
    try {
      const body = {
        ...payload,
        caixas: Object.values($state.selection).map(b => ({
          caixa_ftth: b.caixa_ftth,
          olt: b.olt,
          conexao: b.conexao,
        })),
      };
      const { data } = await aiNetworkMaintenances.create(
        rootGetters.getCurrentAccountId,
        body
      );
      commit(types.SET_CURRENT, data);
      commit(types.SET_SELECTION, {});
      await dispatch('fetchList');
      return data;
    } catch (error) {
      commit(types.SET_ERROR, apiErrorMessage(error));
      throw error;
    } finally {
      commit(types.SET_UI_FLAG, { saving: false });
    }
  },

  // Editar a manutenção (janela, caixa de entrada). Depois do aviso prévio o
  // backend recusa (o público já está prometido) — e a UI trava antes disso.
  async update({ commit, dispatch, rootGetters }, { id, ...payload }) {
    commit(types.SET_UI_FLAG, { saving: true });
    commit(types.SET_ERROR, '');
    try {
      const { data } = await aiNetworkMaintenances.update(
        rootGetters.getCurrentAccountId,
        id,
        payload
      );
      commit(types.SET_CURRENT, data);
      invalidateNoticeAudience(commit, id);
      await dispatch('fetchList');
      return data;
    } catch (error) {
      commit(types.SET_ERROR, apiErrorMessage(error));
      throw error;
    } finally {
      commit(types.SET_UI_FLAG, { saving: false });
    }
  },

  // Encerrar: a obra acabou — a IA deixa de responder por esta manutenção
  // NA HORA (sai do escopo vigente do turn e da página de Comunicados).
  async finish({ commit, dispatch, rootGetters }, id) {
    commit(types.SET_UI_FLAG, { saving: true });
    commit(types.SET_ERROR, '');
    try {
      const { data } = await aiNetworkMaintenances.finish(
        rootGetters.getCurrentAccountId,
        id
      );
      commit(types.SET_CURRENT, data);
      invalidateNoticeAudience(commit, id);
      await dispatch('fetchList');
      return data;
    } catch (error) {
      commit(types.SET_ERROR, apiErrorMessage(error));
      throw error;
    } finally {
      commit(types.SET_UI_FLAG, { saving: false });
    }
  },

  // Reenviar a manutenção pro ERP (write-through da URA). O backend devolve a
  // manutenção com `erp_sync` novo (mesmo shape do show); atualizamos o item na
  // lista e no detalhe aberto pra o badge/banner refletir na hora. 422 com
  // {error} sobe pro chamador (toast.promise divulga).
  async resync({ commit, rootGetters }, maintenanceId) {
    commit(types.SET_UI_FLAG, { resyncing: true });
    commit(types.SET_ERROR, '');
    try {
      const { data } = await aiNetworkMaintenances.resync(
        rootGetters.getCurrentAccountId,
        maintenanceId
      );
      commit(types.UPSERT_MAINTENANCE, data);
      return data;
    } catch (error) {
      commit(types.SET_ERROR, apiErrorMessage(error));
      throw error;
    } finally {
      commit(types.SET_UI_FLAG, { resyncing: false });
    }
  },

  // Recongelar: o congelamento do público é ASSÍNCRONO (job) e a manutenção que
  // falhou fica na lista com o erro à mostra — quem decide tentar de novo é o
  // operador. O POST só re-enfileira (volta pra `pending`); quem conta o
  // desfecho é o evento `maintenance_audience_changed`. 422 com {error} sobe
  // pro chamador (toast.promise divulga).
  async refreeze({ commit, rootGetters }, maintenanceId) {
    commit(types.SET_UI_FLAG, { refreezing: true });
    commit(types.SET_ERROR, '');
    try {
      const { data } = await aiNetworkMaintenances.refreeze(
        rootGetters.getCurrentAccountId,
        maintenanceId
      );
      commit(types.UPSERT_MAINTENANCE, data);
      return data;
    } catch (error) {
      commit(types.SET_ERROR, apiErrorMessage(error));
      throw error;
    } finally {
      commit(types.SET_UI_FLAG, { refreezing: false });
    }
  },

  // Desfecho do congelamento em segundo plano (ActionCable → `massiva.event`).
  // Só funde a linha que JÁ está na lista: sem ela não há o que atualizar (e
  // inventar uma linha a partir de um evento parcial mostraria uma manutenção
  // sem janela nem escopo).
  //
  // O evento NÃO carrega as contagens do público (audience_size/
  // unreachable_size são derivadas no jbuilder), então terminar o
  // congelamento releia a lista — sem isso a coluna "Público" ficaria no 0 de
  // antes do job e o operador leria "manutenção que não afeta ninguém".
  async handleRealtimeEvent({ commit, dispatch, state: $state }, payload) {
    if (payload?.event_type !== 'maintenance_audience_changed') return;

    const data = payload.data || {};
    const id = data.maintenance_id;
    if (!id || !$state.list.some(item => item.id === id)) return;

    commit(types.MERGE_AUDIENCE_STATE, { id, data });

    if (data.audience_status !== 'frozen') return;

    // Falha da releitura PROPAGA (a lista tem endpoint próprio e o operador
    // pode recarregar) — engolir aqui deixaria o número velho passando por
    // congelado.
    await dispatch('fetchList');
    // O detalhe aberto mostra a prévia do público (quem não tem telefone, o
    // teto do canal): ela foi lida com o congelamento ainda em curso.
    if ($state.current?.id === id) {
      // O erro já vira banner no drawer (`audienceError`) — o catch é pra não
      // deixar a rejeição sem dono, não pra esconder.
      await dispatch('fetchAudience', id).catch(() => {});
    }
  },

  // O texto que a IA usa com o afetado que escrever na janela. Endpoint
  // próprio: fica FORA da trava do aviso disparado — o texto pode ser
  // ajustado a qualquer momento, inclusive depois do 1º aviso.
  async updateAiNotice({ commit, dispatch, rootGetters }, { id, ...payload }) {
    commit(types.SET_UI_FLAG, { saving: true });
    commit(types.SET_ERROR, '');
    try {
      const { data } = await aiNetworkMaintenances.aiNotice(
        rootGetters.getCurrentAccountId,
        id,
        payload
      );
      commit(types.SET_CURRENT, data);
      await dispatch('fetchList');
      return data;
    } catch (error) {
      commit(types.SET_ERROR, apiErrorMessage(error));
      throw error;
    } finally {
      commit(types.SET_UI_FLAG, { saving: false });
    }
  },

  // Apagar. O backend recusa (422) se há aviso agendado que ainda não saiu —
  // apagar aqui deixaria o schedule órfão e o operador acharia que avisou.
  // Devolve o `warning` do backend quando o destroy foi liberado SEM remover
  // o registro na URA (provedor trocado etc.) — o chamador mostra o aviso.
  async destroy({ commit, dispatch, rootGetters }, id) {
    commit(types.SET_UI_FLAG, { saving: true });
    commit(types.SET_ERROR, '');
    try {
      const { data } = await aiNetworkMaintenances.destroy(
        rootGetters.getCurrentAccountId,
        id
      );
      commit(types.SET_CURRENT, null);
      commit(types.SET_AUDIENCE, null);
      invalidateNoticeAudience(commit, id);
      await dispatch('fetchList');
      return data?.warning || null;
    } catch (error) {
      commit(types.SET_ERROR, apiErrorMessage(error));
      throw error;
    } finally {
      commit(types.SET_UI_FLAG, { saving: false });
    }
  },

  async fetchAudience({ commit, rootGetters }, id) {
    commit(types.SET_UI_FLAG, { fetchingAudience: true });
    commit(types.SET_AUDIENCE_ERROR, '');
    try {
      const { data } = await aiNetworkMaintenances.audience(
        rootGetters.getCurrentAccountId,
        id
      );
      commit(types.SET_AUDIENCE, data);
    } catch (error) {
      // Sem guardar a mensagem, o drawer mostraria uma prévia VAZIA — e "0
      // clientes" leria como manutenção que não afeta ninguém.
      commit(types.SET_AUDIENCE_ERROR, apiErrorMessage(error));
      throw error;
    } finally {
      commit(types.SET_UI_FLAG, { fetchingAudience: false });
    }
  },

  // O disparo. É o único ponto que fala com o cliente final.
  async notify({ commit, dispatch, rootGetters }, { id, ...args }) {
    commit(types.SET_UI_FLAG, { notifying: true });
    commit(types.SET_ERROR, '');
    try {
      const { data } = await aiNetworkMaintenances.notify(
        rootGetters.getCurrentAccountId,
        id,
        args
      );
      await dispatch('fetchList');
      return data;
    } catch (error) {
      commit(types.SET_ERROR, apiErrorMessage(error));
      throw error;
    } finally {
      commit(types.SET_UI_FLAG, { notifying: false });
    }
  },

  async reschedule({ commit, dispatch, rootGetters }, { id, ...args }) {
    commit(types.SET_UI_FLAG, { saving: true });
    try {
      const { data } = await aiNetworkMaintenances.reschedule(
        rootGetters.getCurrentAccountId,
        id,
        args
      );
      commit(types.SET_CURRENT, data);
      invalidateNoticeAudience(commit, id);
      await dispatch('fetchList');
      return data;
    } catch (error) {
      commit(types.SET_ERROR, apiErrorMessage(error));
      throw error;
    } finally {
      commit(types.SET_UI_FLAG, { saving: false });
    }
  },
};

const mutations = {
  [types.SET_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  [types.SET_LIST]($state, list) {
    $state.list = list;
    // Toda escrita relê a lista. O detalhe ABERTO tem que acompanhar: sem isto,
    // o drawer continuaria dizendo "ninguém avisado" logo depois de o aviso sair.
    // Some da lista (apagada por outro operador) → mantém o que está na tela: o
    // drawer sumindo sozinho seria pior que o dado velho.
    if (!$state.current) return;
    $state.current =
      list.find(item => item.id === $state.current.id) || $state.current;
  },
  [types.SET_URA_SYNC_ENABLED]($state, enabled) {
    $state.uraSyncEnabled = enabled;
  },
  // Opções de um tipo, keyed por tipo. Nova referência (nunca mutação in-place).
  [types.SET_OPCOES]($state, { tipo, data }) {
    $state.opcoesPorTipo = { ...$state.opcoesPorTipo, [tipo]: data };
  },
  // Clientes de um alvo, keyed pela chave tipada. Nova referência.
  [types.SET_CLIENTES]($state, { caixa, data }) {
    $state.clientesPorAlvo = { ...$state.clientesPorAlvo, [caixa]: data };
  },
  // Zera o cache do seletor "tipos" (troca de instância / nova sessão).
  [types.RESET_TIPOS_CACHE]($state) {
    $state.opcoesPorTipo = {};
    $state.clientesPorAlvo = {};
  },
  [types.SET_CURRENT]($state, current) {
    $state.current = current;
  },
  // Atualiza a linha na lista (badge de sync re-renderiza) sem um relist
  // inteiro, e o detalhe aberto se for o mesmo id. Nova referência da lista
  // (nunca mutação in-place).
  [types.UPSERT_MAINTENANCE]($state, item) {
    if (!item) return;
    $state.list = $state.list.map(m => (m.id === item.id ? item : m));
    if ($state.current?.id === item.id) $state.current = item;
  },
  // Desfecho do congelamento assíncrono: funde SÓ os campos do contrato do
  // evento na linha (e no detalhe aberto), sempre com nova referência. Chave
  // ausente no evento não vira `undefined` na linha — mas `audience_error:
  // null` (o job limpou o erro) É uma mudança e tem que passar.
  [types.MERGE_AUDIENCE_STATE]($state, { id, data }) {
    const patch = {};
    AUDIENCE_EVENT_FIELDS.forEach(field => {
      if (field in data) patch[field] = data[field];
    });
    if (!Object.keys(patch).length) return;

    $state.list = $state.list.map(item =>
      item.id === id ? { ...item, ...patch } : item
    );
    if ($state.current?.id === id) {
      $state.current = { ...$state.current, ...patch };
    }
  },
  [types.SET_INSTANCE]($state, instance) {
    $state.instance = instance;
  },
  [types.SET_CIRCUIT]($state, circuit) {
    $state.circuit = circuit;
  },
  [types.SET_CATALOG]($state, catalog) {
    $state.catalog = catalog;
  },
  [types.SET_SELECTION]($state, selection) {
    $state.selection = selection;
  },
  [types.TOGGLE_SELECTION]($state, box) {
    // Nova referência (nunca mutação in-place).
    const next = { ...$state.selection };
    if (next[box.caixa_ftth]) delete next[box.caixa_ftth];
    else next[box.caixa_ftth] = box;
    $state.selection = next;
  },
  [types.SET_AUDIENCE]($state, audience) {
    $state.audience = audience;
  },
  [types.SET_AUDIENCE_ERROR]($state, error) {
    $state.audienceError = error;
  },
  [types.SET_ERROR]($state, error) {
    $state.error = error;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
